import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FormCard, FormGrid, FormSection } from "../FormCard";
import { DetailRow } from "../Rows";
import ConfirmationModal from "../ConfirmationModal";
import LoadingSpinner from "../LoadingSpinner";
import { useAuth, Roles } from "../../providers/AuthContext";
import {
  get_RemoteManagement_certificates,
  get_RemoteManagement_certificates_installed,
  get_RemoteManagement_hubject_v2g_roots,
  post_RemoteManagement_install_v2g_roots,
  post_RemoteManagement_provision_v2g_certificates,
  delete_RemoteManagement_certificates,
  get_RemoteManagement_hubject_registration,
  get_RemoteManagement_hubject_registrations,
  post_RemoteManagement_hubject_registration,
  delete_RemoteManagement_hubject_registration,
} from "../../api/Ocpp201";

const POLL_INTERVAL_MS = 10000;

const CERT_TYPE_ROOT = "V2GRootCertificate";
const CERT_TYPE_LEAF = "V2GCertificate";

const SUCCESS_STATUSES = new Set(["ACTIVE", "VERIFIED"]);
const INSTALLED_ROOT_STATUSES = new Set(["INSTALLED", "VERIFIED", "ACTIVE"]);
const TERMINAL_FAILURE_STATUSES = new Set(["TRIGGER_REJECTED", "CSR_TIMEOUT"]);

const isFailureStatus = (status, isFailure) => {
  if (isFailure) {
    return true;
  }

  const value = String(status || "").toUpperCase();
  return value.endsWith("_FAILED") || TERMINAL_FAILURE_STATUSES.has(value);
};

const isSuccessStatus = (status) => SUCCESS_STATUSES.has(String(status || "").toUpperCase());

const isInstalledRootStatus = (status) =>
  INSTALLED_ROOT_STATUSES.has(String(status || "").toUpperCase());

const hasInstalledV2GRoot = (rows) =>
  (rows ?? []).some(
    (row) => row?.certificateType === CERT_TYPE_ROOT && isInstalledRootStatus(row.status)
  );

const isInProgressStatus = (status, isFailure) => {
  const value = String(status || "").toUpperCase();
  if (!value || value === "REPLACED" || value === "DELETED") {
    return false;
  }

  return !isSuccessStatus(value) && !isFailureStatus(value, isFailure);
};

const hasIssuedCertificate = (certificate) => {
  if (!certificate) {
    return false;
  }

  if (isSuccessStatus(certificate.status)) {
    return true;
  }

  return Boolean(certificate.validToUtc || certificate.validFromUtc || certificate.serialNumberHex);
};

const canDeleteRootCertificate = (certificate) =>
  certificate?.certificateType === CERT_TYPE_ROOT
  && Boolean(certificate?.chargingPointCertificatePublicId && certificate?.serialNumberHex);

const isCurrentLeaf = (row) =>
  row?.certificateType === CERT_TYPE_LEAF
  && !["REPLACED", "DELETED"].includes(String(row.status || "").toUpperCase());

const leafsIssuedByRoot = (root, rows) =>
  (rows ?? []).filter(
    (row) =>
      isCurrentLeaf(row)
      && root?.hubjectRootCertificateId
      && row.issuingHubjectRootCertificateId
      && row.issuingHubjectRootCertificateId === root.hubjectRootCertificateId
  );

const rootIsRetired = (root) =>
  root?.rootRetirementEventType === "root.cert.expired"
  || root?.rootRetirementEventType === "root.cert.revoked";

const canRemoveRoot = (root, rows) =>
  canDeleteRootCertificate(root)
  && (rootIsRetired(root) || leafsIssuedByRoot(root, rows).length === 0);

const leafOnRootLabel = (leaf, t) => {
  if (leaf?.chargingConnectorNumber != null) {
    return t("certificates.connector", { number: leaf.chargingConnectorNumber });
  }

  return leaf?.seccId || leaf?.subjectCommonName || "";
};

const normalizeSerial = (value) => {
  let hex = String(value || "").trim();
  if (hex.toLowerCase().startsWith("0x")) {
    hex = hex.slice(2);
  }

  hex = hex.replace(/^0+/, "");
  return hex.length === 0 ? "0" : hex.toUpperCase();
};

const hasRootOnStation = (rows, installedSerials) => {
  if (!(installedSerials instanceof Set)) {
    return true;
  }

  return (rows ?? []).some(
    (row) =>
      row?.certificateType === CERT_TYPE_ROOT
      && row.serialNumberHex
      && installedSerials.has(normalizeSerial(row.serialNumberHex))
  );
};

const collectInstalledSerials = (response) => {
  const serials = new Set();
  const chains = response?.certificateHashDataChain;
  if (!Array.isArray(chains)) {
    return serials;
  }

  chains.forEach((chain) => {
    const parent = chain?.certificateHashData?.serialNumber;
    if (parent) {
      serials.add(normalizeSerial(parent));
    }

    (chain?.childCertificateHashData ?? []).forEach((child) => {
      if (child?.serialNumber) {
        serials.add(normalizeSerial(child.serialNumber));
      }
    });
  });

  return serials;
};

const chainStatusValue = (certificate, installedSerials, t) => {
  if (!certificate?.serialNumberHex) {
    return "";
  }

  if (installedSerials == null) {
    return "";
  }

  return installedSerials.has(normalizeSerial(certificate.serialNumberHex))
    ? t("certificates.onStation")
    : t("certificates.notOnStation");
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).split("T")[0] || "";
  }

  return date.toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatDateRange = (from, to) => {
  const start = formatDate(from);
  const end = formatDate(to);
  if (start && end) {
    return `${start} – ${end}`;
  }

  return start || end || "";
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MISSING_DATA_KEYS = {
  Manufacturer: "certificates.missingManufacturer",
  Model: "certificates.missingModel",
  "Firmware version": "certificates.missingFirmwareVersion",
  "Serial number": "certificates.missingSerialNumber",
  "OCPP version": "certificates.missingOcppVersion",
};

const OUTCOME_KEYS = {
  REGISTERED: "certificates.outcomeRegistered",
  UPDATED: "certificates.outcomeUpdated",
  UNCHANGED: "certificates.outcomeUnchanged",
  DELETED: "certificates.outcomeDeleted",
  NOT_REGISTERED: "certificates.outcomeNotRegistered",
  FAILED: "certificates.outcomeFailed",
};

const translateMissingData = (value, t) => {
  const key = MISSING_DATA_KEYS[value];
  return key ? t(key) : value;
};

const registrationSuccessMessage = (registration, t) => {
  const outcomes = (registration?.connectors ?? [])
    .map((connector) => connector?.outcome)
    .filter(Boolean);
  if (outcomes.some((outcome) => outcome === "UPDATED")) {
    return t("certificates.outcomeUpdated");
  }

  if (outcomes.length > 0 && outcomes.every((outcome) => outcome === "UNCHANGED")) {
    return t("certificates.outcomeUnchanged");
  }

  return t("certificates.registeredWithHubject");
};

const translateOutcome = (outcome, t) => {
  if (!outcome) {
    return "";
  }

  const key = OUTCOME_KEYS[outcome];
  return key ? t(key) : outcome;
};

const normalizeCertificateList = (data) => {
  if (Array.isArray(data)) {
    return { certificates: data, v2gRootUpdate: null };
  }

  return {
    certificates: Array.isArray(data?.certificates) ? data.certificates : [],
    v2gRootUpdate: data?.v2gRootUpdate ?? null,
  };
};

const normalizeFingerprint = (value) =>
  String(value || "").replace(/[\s:]/g, "").toLowerCase();

const matchesRootUpdate = (certificate, update) => {
  if (!certificate || !update || certificate.certificateType !== CERT_TYPE_ROOT) {
    return false;
  }

  if (
    update.rootCertificateId
    && certificate.rootCertificateId
    && certificate.rootCertificateId === update.rootCertificateId
  ) {
    return true;
  }

  const updateFingerprint = normalizeFingerprint(update.fingerprintSha256);
  const certificateFingerprint = normalizeFingerprint(certificate.fingerprintSha256);
  return updateFingerprint !== "" && updateFingerprint === certificateFingerprint;
};

const rootUpdateNeedsRemoval = (update) =>
  update?.eventType === "root.cert.expired" || update?.eventType === "root.cert.revoked";

const hasRootUpdate = (update) =>
  Boolean(update && (update.eventType || update.commonName || update.required));

const rootUpdateText = (update, t) => {
  const name = update?.commonName || update?.fingerprintSha256 || "";
  if (update?.eventType === "root.cert.added") {
    return t("certificates.rootAdded", { name });
  }

  if (update?.eventType === "root.cert.expired") {
    return t("certificates.rootExpired", { name });
  }

  if (update?.eventType === "root.cert.revoked") {
    return t("certificates.rootRevoked", { name });
  }

  return update?.eventType || "";
};

const registrationHasFailures = (registration) =>
  (registration?.connectors ?? []).some((connector) => connector?.outcome === "FAILED");

const registrationFailureText = (registration) =>
  (registration?.connectors ?? [])
    .filter((connector) => connector?.outcome === "FAILED")
    .map((connector) => connector.error)
    .filter(Boolean)
    .join("; ");

const pickLatestLeaf = (rows) => {
  const latestByKey = new Map();

  rows
    .filter((row) => row?.certificateType === CERT_TYPE_LEAF && !["REPLACED", "DELETED"].includes(String(row.status || "").toUpperCase()))
    .forEach((row) => {
      const key = row.chargingConnectorPublicId || row.seccId || row.chargingPointCertificatePublicId;
      const existing = latestByKey.get(key);
      if (!existing) {
        latestByKey.set(key, row);
        return;
      }

      const existingCreated = new Date(existing.createdDateTimeUtc || 0).getTime();
      const nextCreated = new Date(row.createdDateTimeUtc || 0).getTime();
      if (nextCreated >= existingCreated) {
        latestByKey.set(key, row);
      }
    });

  return [...latestByKey.values()];
};

const buildConnectorRows = (chargingPoint, certificates) => {
  const connectors = chargingPoint?.connectors ?? chargingPoint?.chargingConnectors ?? [];
  const latestLeaves = pickLatestLeaf(certificates);
  const usedIds = new Set();

  const fromConnectors = [...connectors]
    .sort((a, b) => (a.chargingConnectorNumber ?? 0) - (b.chargingConnectorNumber ?? 0))
    .map((connector) => {
      const certificate = latestLeaves.find(
        (row) => row.chargingConnectorPublicId === connector.chargingConnectorPublicId
      );
      if (certificate) {
        usedIds.add(certificate.chargingPointCertificatePublicId);
      }

      return {
        connectorNumber: connector.chargingConnectorNumber,
        chargingConnectorPublicId: connector.chargingConnectorPublicId,
        seccId: certificate?.seccId,
        certificate: certificate ?? null,
      };
    });

  const extras = latestLeaves
    .filter((row) => !usedIds.has(row.chargingPointCertificatePublicId))
    .sort((a, b) => (a.chargingConnectorNumber ?? 0) - (b.chargingConnectorNumber ?? 0))
    .map((row) => ({
      connectorNumber: row.chargingConnectorNumber,
      chargingConnectorPublicId: row.chargingConnectorPublicId,
      seccId: row.seccId,
      certificate: row,
    }));

  return [...fromConnectors, ...extras];
};

const statusTone = (status, isFailure, installedIsSuccess) => {
  if (isFailureStatus(status, isFailure)) {
    return "error";
  }

  if (isSuccessStatus(status)) {
    return "success";
  }

  if (installedIsSuccess && INSTALLED_ROOT_STATUSES.has(String(status || "").toUpperCase())) {
    return "success";
  }

  if (!status) {
    return "mute";
  }

  return "pending";
};

const CertificateStatusBadge = ({ status, isFailure, installedIsSuccess = false }) => {
  if (!status) {
    return <span className="text-sm">-</span>;
  }

  const tone = statusTone(status, isFailure, installedIsSuccess);
  const classes = {
    success: "bg-secondary-100 text-secondary-500",
    pending: "bg-warning-100 text-warning-700",
    error: "bg-error-100 text-error-500",
    mute: "bg-mute-100 text-mute-500",
  };

  return (
    <span className={`px-2 py-1 rounded-md text-xs font-bold ${classes[tone]}`}>
      {status}
    </span>
  );
};

const CertificateGuide = ({ t }) => (
  <FormCard title={t("certificates.guideTitle")} bodyClassName="flex flex-col gap-6">
    <p className="text-sm">{t("certificates.guideIntro")}</p>
    <div>
      <h3 className="text-base font-semibold">{t("certificates.guideFlowTitle")}</h3>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm">
        <li>{t("certificates.guideFlow1")}</li>
        <li>{t("certificates.guideFlow2")}</li>
        <li>{t("certificates.guideFlow3")}</li>
        <li>{t("certificates.guideFlow4")}</li>
      </ol>
    </div>
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold">{t("certificates.guideSectionsTitle")}</h3>
      <div>
        <p className="text-sm font-semibold">{t("certificates.guideRegistrationTitle")}</p>
        <p className="mt-1 text-sm">{t("certificates.guideRegistrationLead")}</p>
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
          <li>{t("certificates.guideRegistrationRegister")}</li>
          <li>{t("certificates.guideRegistrationUpdate")}</li>
          <li>{t("certificates.guideRegistrationList")}</li>
          <li>{t("certificates.guideRegistrationRemove")}</li>
        </ul>
      </div>
      <div>
        <p className="text-sm font-semibold">{t("certificates.guideTrustTitle")}</p>
        <p className="mt-1 text-sm">{t("certificates.guideTrustLead")}</p>
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
          <li>{t("certificates.guideTrustCreate")}</li>
          <li>{t("certificates.guideTrustNew")}</li>
          <li>{t("certificates.guideTrustDelete")}</li>
        </ul>
      </div>
      <div>
        <p className="text-sm font-semibold">{t("certificates.guideConnectorsTitle")}</p>
        <p className="mt-1 text-sm">{t("certificates.guideConnectorsBody")}</p>
      </div>
      <div>
        <p className="text-sm font-semibold">{t("certificates.guideCreateTitle")}</p>
        <p className="mt-1 text-sm">{t("certificates.guideCreateLead")}</p>
        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
          <li>{t("certificates.guideCreateFirst")}</li>
          <li>{t("certificates.guideCreateRenew")}</li>
          <li>{t("certificates.guideCreateWait")}</li>
        </ul>
      </div>
    </div>
  </FormCard>
);

const PointCertificates = ({ chargingPoint }) => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const isAdmin = hasRole(Roles.Admin);
  const [certificates, setCertificates] = useState([]);
  const [v2gRootUpdate, setV2gRootUpdate] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [registeredEndEntities, setRegisteredEndEntities] = useState(null);
  const [loadingEndEntities, setLoadingEndEntities] = useState(false);
  const [installedSerials, setInstalledSerials] = useState(null);
  const [fetchedRoots, setFetchedRoots] = useState(null);
  const [loadingHubjectRoots, setLoadingHubjectRoots] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    okText: undefined,
    confirm: () => {},
  });
  const pollingRef = useRef(null);
  const chargingPointPublicIdRef = useRef(chargingPoint?.chargingPointPublicId);

  const chargingPointPublicId = chargingPoint?.chargingPointPublicId;
  chargingPointPublicIdRef.current = chargingPointPublicId;
  const isOffline = chargingPoint?.isOnline === false;

  const clearPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const loadCertificates = useCallback(
    async ({ silent = false, refreshStation = !silent, refreshRegistration = !silent } = {}) => {
      if (!chargingPointPublicId) {
        if (!silent) {
          setLoading(false);
        }

        return [];
      }

      const requestedPointId = chargingPointPublicId;
      const isCurrentPoint = () => chargingPointPublicIdRef.current === requestedPointId;

      try {
        const accessToken = await getAccessToken();
        const [certificateResult, registrationResult] = await Promise.allSettled([
          get_RemoteManagement_certificates(accessToken, chargingPointPublicId),
          refreshRegistration
            ? get_RemoteManagement_hubject_registration(accessToken, chargingPointPublicId)
            : Promise.resolve(undefined),
        ]);

        if (!isCurrentPoint()) {
          return [];
        }

        if (refreshRegistration) {
          if (registrationResult.status === "fulfilled") {
            setRegistration(registrationResult.value ?? null);
          } else if (registrationResult.status === "rejected" && !silent) {
            toast.error(registrationResult.reason?.message || t("certificates.registrationLoadFailed"));
          }
        }

        if (certificateResult.status === "rejected") {
          if (!silent) {
            toast.error(certificateResult.reason?.message || t("certificates.loadFailed"));
          }

          return [];
        }

        const parsed = normalizeCertificateList(certificateResult.value);
        setCertificates(parsed.certificates);
        setV2gRootUpdate(parsed.v2gRootUpdate);

        if (refreshStation && chargingPoint?.isOnline !== false) {
          try {
            const installed = await get_RemoteManagement_certificates_installed(
              accessToken,
              chargingPointPublicId
            );
            if (!isCurrentPoint()) {
              return [];
            }

            setInstalledSerials(collectInstalledSerials(installed));
          } catch (error) {
            if (!isCurrentPoint()) {
              return [];
            }

            setInstalledSerials(null);
            if (!silent) {
              toast.error(error?.message || t("certificates.readChargerFailed"));
            }
          }
        }

        return parsed.certificates;
      } catch (error) {
        if (isCurrentPoint() && !silent) {
          toast.error(error?.message || t("certificates.loadFailed"));
        }

        return [];
      } finally {
        if (!silent && isCurrentPoint()) {
          setLoading(false);
        }
      }
    },
    [chargingPointPublicId, chargingPoint?.isOnline, getAccessToken, t]
  );

  useEffect(() => {
    setCertificates([]);
    setV2gRootUpdate(null);
    setFetchedRoots(null);
    setInstalledSerials(null);
    setRegistration(null);
    setRegisteredEndEntities(null);
  }, [chargingPointPublicId]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) {
      return;
    }

    pollingRef.current = setInterval(() => {
      loadCertificates({ silent: true, refreshStation: false });
    }, POLL_INTERVAL_MS);
  }, [loadCertificates]);

  useEffect(() => {
    setLoading(true);
    loadCertificates().then((rows) => {
      if (pickLatestLeaf(rows).some((row) => isInProgressStatus(row.status, row.isFailure))) {
        startPolling();
      }
    });

    return () => {
      clearPolling();
    };
  }, [loadCertificates, startPolling]);

  useEffect(() => {
    const inProgress = pickLatestLeaf(certificates).some((row) =>
      isInProgressStatus(row.status, row.isFailure)
    );

    if (inProgress) {
      startPolling();
      return;
    }

    if (pollingRef.current) {
      clearPolling();
      loadCertificates({ silent: true, refreshStation: true });
    }
  }, [certificates, startPolling, loadCertificates]);

  const roots = certificates.filter(
    (row) =>
      row.certificateType === CERT_TYPE_ROOT
      && !["REPLACED", "DELETED"].includes(String(row.status || "").toUpperCase())
  );
  const connectorRows = buildConnectorRows(chargingPoint, certificates);
  const hasInProgressRun = pickLatestLeaf(certificates).some((row) =>
    isInProgressStatus(row.status, row.isFailure)
  );

  useEffect(() => {
    if (!hasInProgressRun) {
      toast.dismiss("certificate-provisioning");
      return;
    }

    toast.loading(t("certificates.provisioning"), { id: "certificate-provisioning" });
    return () => {
      toast.dismiss("certificate-provisioning");
    };
  }, [hasInProgressRun, t]);
  const showSpinner = loading;
  const registrationConnectors = [...(registration?.connectors ?? [])].sort(
    (a, b) => (a.chargingConnectorNumber ?? 0) - (b.chargingConnectorNumber ?? 0)
  );
  const missingHubjectData = Array.isArray(registration?.missingData) ? registration.missingData : [];
  const isHubjectRegistered = registration?.isRegistered === true;
  const canRemovePointRegistration = isAdmin && (
    registration?.isRegistered || registrationConnectors.some((connector) => connector?.isRegistered)
  );
  const registerDisabled = missingHubjectData.length > 0 || isSubmitting || !chargingPointPublicId;
  const provisionDisabled =
    isOffline || hasInProgressRun || isSubmitting || !chargingPointPublicId || !isHubjectRegistered;
  const showRootUpdate = hasRootUpdate(v2gRootUpdate);
  const matchingRootUpdate = showRootUpdate
    ? roots.find((root) => matchesRootUpdate(root, v2gRootUpdate))
    : null;
  const rootAlreadyInstalled = (root) =>
    roots.some((installed) => {
      if (!isInstalledRootStatus(installed.status)) {
        return false;
      }

      if (
        root?.rootCertificateId
        && installed.hubjectRootCertificateId
        && installed.hubjectRootCertificateId === root.rootCertificateId
      ) {
        return true;
      }

      const left = normalizeFingerprint(root?.fingerprintSha256);
      const right = normalizeFingerprint(installed.fingerprintSha256);
      return left !== "" && left === right;
    });
  const visibleFetchedRoots = (fetchedRoots ?? []).filter((root) => {
    if (rootAlreadyInstalled(root)) {
      return false;
    }

    if (v2gRootUpdate?.eventType !== "root.cert.added") {
      return true;
    }

    return !matchesRootUpdate(
      {
        certificateType: CERT_TYPE_ROOT,
        rootCertificateId: root.rootCertificateId,
        fingerprintSha256: root.fingerprintSha256,
      },
      v2gRootUpdate
    );
  });
  const issuedConnectorCount = connectorRows.filter((connector) =>
    hasIssuedCertificate(connector.certificate)
  ).length;
  const chargerActionLabel =
    connectorRows.length > 0 && issuedConnectorCount === connectorRows.length
      ? t("certificates.renewCertificates")
      : issuedConnectorCount > 0
        ? t("certificates.createOrRenew")
        : t("certificates.createCertificates");

  const closeConfirm = () => {
    setConfirmState((s) => ({ ...s, isOpen: false, okText: undefined }));
  };

  const askConfirm = (title, okText, confirm) => {
    setConfirmState({
      isOpen: true,
      title,
      okText,
      confirm,
    });
  };

  const installV2GRootsIfNeeded = async (accessToken, rows) => {
    if (hasInstalledV2GRoot(rows) && hasRootOnStation(rows, installedSerials)) {
      return;
    }

    const installed = await post_RemoteManagement_install_v2g_roots(accessToken, {
      chargingPointPublicId,
    });
    const normalized = Array.isArray(installed) ? installed : [];
    if (!hasInstalledV2GRoot(normalized)) {
      const failed = normalized.find((row) => row?.failureReason);
      throw new Error(failed?.failureReason || t("certificates.rootNotInstalled"));
    }
  };

  const provision = async (chargingConnectorPublicId) => {
    if (provisionDisabled) {
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(t("certificates.installingRoot"));

    try {
      const accessToken = await getAccessToken();
      await installV2GRootsIfNeeded(accessToken, certificates);

      toast.loading(t("certificates.startingProvisioning"), { id: toastId });
      const payload = { chargingPointPublicId };
      if (chargingConnectorPublicId) {
        payload.evsePublicId = chargingConnectorPublicId;
      }

      await post_RemoteManagement_provision_v2g_certificates(accessToken, payload);
      toast.success(t("certificates.provisioningStarted"), { id: toastId });
      const rows = await loadCertificates({ silent: true, refreshStation: true });
      if (pickLatestLeaf(rows).some((row) => isInProgressStatus(row.status, row.isFailure))) {
        startPolling();
      }
    } catch (error) {
      toast.error(error?.message || t("certificates.provisionFailed"), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestProvision = (chargingConnectorPublicId, action, name) => {
    if (provisionDisabled) {
      return;
    }

    if (action === "create") {
      provision(chargingConnectorPublicId);
      return;
    }

    const title = chargingConnectorPublicId
      ? t("certificates.renewConnectorConfirm", { name })
      : t("certificates.renewPointConfirm");
    askConfirm(title, t("certificates.yesRenew"), () => provision(chargingConnectorPublicId));
  };

  const deleteCertificate = async (chargingPointCertificatePublicId) => {
    if (isOffline || isSubmitting || !chargingPointPublicId || !chargingPointCertificatePublicId) {
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(t("certificates.deleting"));

    try {
      const accessToken = await getAccessToken();
      await delete_RemoteManagement_certificates(
        accessToken,
        chargingPointPublicId,
        chargingPointCertificatePublicId
      );
      toast.success(t("certificates.deleted"), { id: toastId });
      await loadCertificates({ silent: true, refreshStation: true });
    } catch (error) {
      toast.error(error?.message || t("certificates.deleteFailed"), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestDelete = (certificate, name) => {
    if (isOffline || isSubmitting || !canRemoveRoot(certificate, certificates)) {
      return;
    }

    askConfirm(
      t("certificates.deleteConfirm", { name }),
      t("certificates.yesDelete"),
      () => deleteCertificate(certificate.chargingPointCertificatePublicId)
    );
  };

  const showRegistrationResult = (nextRegistration, successMessage, failureMessage, toastId) => {
    if (nextRegistration) {
      setRegistration(nextRegistration);
    }

    if (registrationHasFailures(nextRegistration)) {
      toast.error(registrationFailureText(nextRegistration) || failureMessage, { id: toastId });
      return;
    }

    toast.success(successMessage, { id: toastId });
  };

  const registerWithHubject = async () => {
    if (registerDisabled) {
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(
      t(isHubjectRegistered ? "certificates.updatingRegistration" : "certificates.registering")
    );

    try {
      const accessToken = await getAccessToken();
      const nextRegistration = await post_RemoteManagement_hubject_registration(accessToken, {
        chargingPointPublicId,
      });
      showRegistrationResult(
        nextRegistration,
        registrationSuccessMessage(nextRegistration, t),
        t("certificates.registerFailed"),
        toastId
      );
    } catch (error) {
      toast.error(error?.message || t("certificates.registerFailed"), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeHubjectRegistration = async (chargingConnectorPublicId) => {
    if (!isAdmin || isSubmitting || !chargingPointPublicId) {
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(t("certificates.removingRegistration"));

    try {
      const accessToken = await getAccessToken();
      const nextRegistration = await delete_RemoteManagement_hubject_registration(
        accessToken,
        chargingPointPublicId,
        chargingConnectorPublicId
      );
      showRegistrationResult(
        nextRegistration,
        t("certificates.registrationRemoved"),
        t("certificates.removeRegistrationFailed"),
        toastId
      );
    } catch (error) {
      toast.error(error?.message || t("certificates.removeRegistrationFailed"), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadRegisteredEndEntities = async () => {
    if (loadingEndEntities) {
      return;
    }

    setLoadingEndEntities(true);
    const toastId = toast.loading(t("certificates.loadingRegisteredEvses"));

    try {
      const accessToken = await getAccessToken();
      const result = await get_RemoteManagement_hubject_registrations(accessToken);
      const entities = Array.isArray(result?.endEntities) ? result.endEntities : [];
      setRegisteredEndEntities(entities);
      toast.success(t("certificates.registeredEvsesCount", { count: result?.totalCount ?? entities.length }), { id: toastId });
    } catch (error) {
      toast.error(error?.message || t("certificates.registeredEvsesFailed"), { id: toastId });
    } finally {
      setLoadingEndEntities(false);
    }
  };

  const requestRemovePointRegistration = () => {
    if (!canRemovePointRegistration || isSubmitting) {
      return;
    }

    askConfirm(
      t("certificates.removePointConfirm"),
      t("certificates.yesRemoveRegistration"),
      () => removeHubjectRegistration()
    );
  };

  const requestRemoveConnectorRegistration = (connector) => {
    if (!isAdmin || isSubmitting || !connector?.isRegistered || !connector.chargingConnectorPublicId) {
      return;
    }

    askConfirm(
      t("certificates.removeConnectorConfirm", { number: connector.chargingConnectorNumber ?? "-" }),
      t("certificates.yesRemoveRegistration"),
      () => removeHubjectRegistration(connector.chargingConnectorPublicId)
    );
  };

  const fetchHubjectRoots = async () => {
    if (loadingHubjectRoots || !chargingPointPublicId) {
      return;
    }

    setLoadingHubjectRoots(true);
    const toastId = toast.loading(t("certificates.fetchingHubjectRoots"));

    try {
      const accessToken = await getAccessToken();
      const result = await get_RemoteManagement_hubject_v2g_roots(accessToken, chargingPointPublicId);
      const rootsFromHubject = Array.isArray(result) ? result : [];
      setFetchedRoots(rootsFromHubject);
      toast.success(t("certificates.hubjectRootsFetched", { count: rootsFromHubject.length }), { id: toastId });
    } catch (error) {
      toast.error(error?.message || t("certificates.hubjectRootsFailed"), { id: toastId });
    } finally {
      setLoadingHubjectRoots(false);
    }
  };

  const installFetchedRoot = async (root) => {
    if (isOffline || isSubmitting || !chargingPointPublicId || !root?.rootCertificateId) {
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(t("certificates.installingRoot"));

    try {
      const accessToken = await getAccessToken();
      const installed = await post_RemoteManagement_install_v2g_roots(accessToken, {
        chargingPointPublicId,
        rootCertificateId: root.rootCertificateId,
      });
      const normalized = Array.isArray(installed) ? installed : [];
      const failed = normalized.find((row) => row?.failureReason || isFailureStatus(row?.status, row?.isFailure));
      if (normalized.length > 0 && failed && !hasInstalledV2GRoot(normalized)) {
        throw new Error(failed.failureReason || t("certificates.rootNotInstalled"));
      }

      setFetchedRoots((current) =>
        (current ?? []).filter((item) => item.rootCertificateId !== root.rootCertificateId)
      );
      toast.success(t("certificates.rootInstalled"), { id: toastId });
      await loadCertificates({ silent: true, refreshStation: true });
    } catch (error) {
      toast.error(error?.message || t("certificates.rootNotInstalled"), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const installAnnouncedRoot = async () => {
    if (isOffline || isSubmitting || !chargingPointPublicId) {
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(t("certificates.installingRoot"));

    try {
      const accessToken = await getAccessToken();
      const installed = await post_RemoteManagement_install_v2g_roots(accessToken, {
        chargingPointPublicId,
      });
      const normalized = Array.isArray(installed) ? installed : [];
      const failed = normalized.find((row) => row?.failureReason || isFailureStatus(row?.status, row?.isFailure));
      if (normalized.length > 0 && failed && !hasInstalledV2GRoot(normalized)) {
        throw new Error(failed.failureReason || t("certificates.rootNotInstalled"));
      }

      toast.success(t("certificates.rootInstalled"), { id: toastId });
      await loadCertificates({ silent: true, refreshStation: true });
    } catch (error) {
      toast.error(error?.message || t("certificates.rootNotInstalled"), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-bold">{t("certificates.title")}</h2>
        <button
          type="button"
          className="btn-sm btn-outline-primary"
          aria-expanded={showGuide}
          aria-label={t("certificates.guideButtonLabel")}
          onClick={() => setShowGuide((open) => !open)}
        >
          <FontAwesomeIcon icon={faInfoCircle} className="h-3.5 w-3.5" />
          {t("certificates.guideButton")}
        </button>
      </div>
      {showGuide ? <CertificateGuide t={t} /> : null}
      {showSpinner ? (
        <LoadingSpinner text={t("certificates.loading")} delayMs={0} />
      ) : (
        <>
          <FormSection title={t("certificates.hubjectRegistration")}>
            <FormCard
              description={missingHubjectData.length > 0 ? t("certificates.missingDataHint") : undefined}
              bodyClassName="flex flex-col"
            >
              <DetailRow
                label={t("common.status")}
                value={isHubjectRegistered ? t("certificates.registered") : t("certificates.notRegistered")}
              />
              {missingHubjectData.length > 0 ? (
                <DetailRow
                  label={t("certificates.missingData")}
                  value={missingHubjectData.map((item) => translateMissingData(item, t)).join(", ")}
                />
              ) : null}
              <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                {canRemovePointRegistration ? (
                  <button
                    className="btn btn-outline-primary"
                    disabled={isSubmitting}
                    onClick={requestRemovePointRegistration}
                  >
                    {t("certificates.removeRegistration")}
                  </button>
                ) : null}
                <button
                  className="btn btn-outline-primary"
                  disabled={loadingEndEntities}
                  onClick={loadRegisteredEndEntities}
                >
                  {t("certificates.showRegisteredEvses")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={registerDisabled}
                  onClick={registerWithHubject}
                >
                  {t(isHubjectRegistered ? "certificates.updateRegistration" : "certificates.registerWithHubject")}
                </button>
              </div>
            </FormCard>
            {registrationConnectors.length > 0 ? (
              <FormGrid className="gap-6 mt-6">
                {registrationConnectors.map((connector) => {
                  const title = t("certificates.connector", {
                    number: connector.chargingConnectorNumber ?? "-",
                  });
                  return (
                    <FormCard
                      key={connector.chargingConnectorPublicId || title}
                      title={title}
                      description={
                        connector.isRemovedFromChargingPoint
                          ? t("certificates.removedFromPoint")
                          : undefined
                      }
                      bodyClassName="flex flex-col"
                    >
                      <DetailRow
                        label={t("common.status")}
                        value={
                          connector.isRegistered
                            ? t("certificates.registered")
                            : t("certificates.notRegistered")
                        }
                      />
                      <DetailRow label="SECC-ID" value={connector.seccId} />
                      <DetailRow
                        label={t("certificates.registeredAt")}
                        value={formatDateTime(connector.registeredDateTimeUtc)}
                      />
                      {connector.outcome ? (
                        <DetailRow
                          label={t("certificates.result")}
                          value={translateOutcome(connector.outcome, t)}
                        />
                      ) : null}
                      {connector.outcome === "FAILED" && connector.error ? (
                        <DetailRow label={t("common.error")} value={connector.error} />
                      ) : null}
                      {isAdmin && connector.isRegistered ? (
                        <div className="flex justify-end gap-2 pt-4">
                          <button
                            className="btn btn-outline-primary"
                            disabled={isSubmitting}
                            onClick={() => requestRemoveConnectorRegistration(connector)}
                          >
                            {t("certificates.removeConnectorRegistration")}
                          </button>
                        </div>
                      ) : null}
                    </FormCard>
                  );
                })}
              </FormGrid>
            ) : null}
            {registeredEndEntities ? (
              <div className="mt-6 flex flex-col gap-6">
                <p className="text-sm text-mute-500">{t("certificates.registeredEvsesHint")}</p>
                {registeredEndEntities.length === 0 ? (
                  <p className="text-sm">{t("certificates.noRegisteredEvses")}</p>
                ) : (
                  <FormGrid className="gap-6">
                    {registeredEndEntities.map((entity, index) => {
                      const evseIds = Array.isArray(entity.evseIds) ? entity.evseIds.filter(Boolean).join(", ") : "";
                      return (
                        <FormCard
                          key={entity.commonName || `${evseIds}-${index}`}
                          title={entity.commonName || t("certificates.showRegisteredEvses")}
                          bodyClassName="flex flex-col"
                        >
                          <DetailRow label={t("certificates.missingManufacturer")} value={entity.manufacture} />
                          <DetailRow label={t("certificates.missingModel")} value={entity.deviceName} />
                          <DetailRow label={t("certificates.missingFirmwareVersion")} value={entity.deviceSwVersion} />
                          <DetailRow label={t("certificates.missingSerialNumber")} value={entity.evseSerialNumber} />
                          <DetailRow label={t("certificates.evseIds")} value={evseIds} />
                          <DetailRow label={t("certificates.isoVersion")} value={entity.evseIsoVersion} />
                          <DetailRow label={t("certificates.missingOcppVersion")} value={entity.ocppVersion} />
                          <DetailRow label={t("certificates.chargeBoxSerial")} value={entity.chargeBoxSerialNumber} />
                          <DetailRow label={t("certificates.registeredAt")} value={formatDateTime(entity.registeredDate)} />
                        </FormCard>
                      );
                    })}
                  </FormGrid>
                )}
              </div>
            ) : null}
          </FormSection>

          <FormSection title={t("certificates.trustStore")}>
            <div className="mb-6">
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={loadingHubjectRoots || isSubmitting || !chargingPointPublicId}
                onClick={fetchHubjectRoots}
              >
                {t("certificates.fetchHubjectRoots")}
              </button>
            </div>
            {fetchedRoots && fetchedRoots.length === 0 ? (
              <p className="mb-6 text-sm text-mute-500">{t("certificates.noNewHubjectRoots")}</p>
            ) : null}
            {visibleFetchedRoots.map((root) => (
              <FormCard
                key={root.rootCertificateId || root.fingerprintSha256}
                title={t("certificates.rootUpdateTitle")}
                description={rootUpdateText({ ...root, eventType: root.eventType || "root.cert.added" }, t)}
                className="mb-6"
                bodyClassName="flex flex-col"
              >
                <DetailRow label={t("certificates.subject")} value={root.commonName} />
                <DetailRow label={t("certificates.fingerprint")} value={root.fingerprintSha256} />
                {isOffline ? (
                  <p className="pt-4 text-sm text-mute-500">{t("certificates.chargerMustBeOnline")}</p>
                ) : null}
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isOffline || isSubmitting || !root.rootCertificateId}
                    onClick={() => installFetchedRoot(root)}
                  >
                    {t("certificates.installRoot")}
                  </button>
                </div>
              </FormCard>
            ))}
            {showRootUpdate ? (
              <FormCard
                title={t("certificates.rootUpdateTitle")}
                description={rootUpdateText(v2gRootUpdate, t)}
                className="mb-6"
                bodyClassName="flex flex-col"
              >
                <DetailRow label={t("certificates.subject")} value={v2gRootUpdate.commonName} />
                <DetailRow
                  label={t("certificates.fingerprint")}
                  value={v2gRootUpdate.fingerprintSha256}
                />
                {rootUpdateNeedsRemoval(v2gRootUpdate) && !matchingRootUpdate ? (
                  <p className="pt-4 text-sm text-mute-500">{t("certificates.rootNotFound")}</p>
                ) : null}
                {isOffline ? (
                  <p className="pt-4 text-sm text-mute-500">{t("certificates.chargerMustBeOnline")}</p>
                ) : null}
                {v2gRootUpdate.eventType === "root.cert.added" || (rootUpdateNeedsRemoval(v2gRootUpdate) && canRemoveRoot(matchingRootUpdate, certificates)) ? (
                  <div className="flex justify-end gap-2 pt-4">
                    {v2gRootUpdate.eventType === "root.cert.added" ? (
                      <button
                        className="btn btn-primary"
                        disabled={isOffline || isSubmitting}
                        onClick={installAnnouncedRoot}
                      >
                        {t("certificates.installRoot")}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-primary"
                        disabled={isOffline || isSubmitting || hasInProgressRun}
                        onClick={() => requestDelete(matchingRootUpdate, v2gRootUpdate.commonName || t("certificates.v2gRoot"))}
                      >
                        {t("common.delete")}
                      </button>
                    )}
                  </div>
                ) : null}
              </FormCard>
            ) : null}
            <FormGrid className="gap-6">
              {roots.length === 0 ? (
                <FormCard title={t("certificates.v2gRoot")} bodyClassName="flex flex-col">
                  <DetailRow label={t("common.status")} value={t("certificates.notInstalled")} />
                </FormCard>
              ) : (
                roots.map((root) => {
                  const installed = INSTALLED_ROOT_STATUSES.has(String(root.status || "").toUpperCase());
                  const failed = isFailureStatus(root.status, root.isFailure);
                  return (
                    <FormCard
                      key={root.chargingPointCertificatePublicId}
                      title={t("certificates.v2gRoot")}
                      bodyClassName="flex flex-col"
                    >
                      <DetailRow
                        label={t("common.status")}
                        value={
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{installed ? t("certificates.installed") : root.status || t("certificates.notInstalled")}</span>
                            <CertificateStatusBadge
                              status={root.status}
                              isFailure={root.isFailure}
                              installedIsSuccess
                            />
                          </span>
                        }
                      />
                      <DetailRow label={t("certificates.subject")} value={root.subjectCommonName} />
                      <DetailRow label={t("certificates.serial")} value={root.serialNumberHex} />
                      <DetailRow label={t("table.validFrom")} value={formatDate(root.validFromUtc)} />
                      <DetailRow label={t("table.validTo")} value={formatDate(root.validToUtc)} />
                      <DetailRow
                        label={t("certificates.fingerprint")}
                        value={root.fingerprintSha256}
                      />
                      {leafsIssuedByRoot(root, certificates).length > 0 ? (
                        <DetailRow
                          label={t("certificates.leafsOnRoot")}
                          value={leafsIssuedByRoot(root, certificates).map((leaf) => leafOnRootLabel(leaf, t)).filter(Boolean).join(", ")}
                        />
                      ) : null}
                      {rootIsRetired(root) && leafsIssuedByRoot(root, certificates).length > 0 ? (
                        <p className="pt-4 text-sm text-mute-500">{t("certificates.renewBeforeRemovingRoot")}</p>
                      ) : null}
                      {failed && root.failureReason ? (
                        <DetailRow label={t("common.error")} value={root.failureReason} />
                      ) : null}
                      <DetailRow
                        label={t("certificates.chainStatus")}
                        value={chainStatusValue(root, installedSerials, t)}
                      />
                      {canRemoveRoot(root, certificates) ? (
                        <div className="flex justify-end gap-2 pt-4">
                          <button
                            className="btn btn-outline-primary"
                            disabled={isOffline || isSubmitting || hasInProgressRun}
                            onClick={() => requestDelete(root, t("certificates.v2gRoot"))}
                          >
                            {t("common.delete")}
                          </button>
                        </div>
                      ) : null}
                    </FormCard>
                  );
                })
              )}
            </FormGrid>
          </FormSection>

          <FormSection title={t("certificates.connectors")}>
            {connectorRows.length === 0 ? (
              <p className="text-sm text-mute-500">{t("certificates.noConnectors")}</p>
            ) : (
              <FormGrid className="gap-6">
                {connectorRows.map((connector) => {
                  const certificate = connector.certificate;
                  const failed = isFailureStatus(certificate?.status, certificate?.isFailure);
                  const title = t("certificates.connector", { number: connector.connectorNumber ?? "-" });
                  const seccId = certificate?.seccId || connector.seccId;
                  const canRenew = hasIssuedCertificate(certificate);

                  return (
                    <FormCard
                      key={connector.chargingConnectorPublicId || seccId || title}
                      title={title}
                      description={seccId ? t("certificates.seccId", { id: seccId }) : undefined}
                      bodyClassName="flex flex-col"
                    >
                      <DetailRow
                        label={t("common.status")}
                        value={
                          certificate ? (
                            <span className="inline-flex flex-wrap items-center gap-2">
                              <CertificateStatusBadge
                                status={certificate.status}
                                isFailure={certificate.isFailure}
                              />
                              {isInProgressStatus(certificate.status, certificate.isFailure) ? (
                                <span className="text-sm font-normal text-mute-500">{t("certificates.inProgress")}</span>
                              ) : null}
                            </span>
                          ) : (
                            t("certificates.notProvisioned")
                          )
                        }
                      />
                      <DetailRow label="SECC-ID" value={seccId} />
                      <DetailRow
                        label={t("certificates.valid")}
                        value={formatDateRange(certificate?.validFromUtc, certificate?.validToUtc)}
                      />
                      <DetailRow label={t("certificates.serial")} value={certificate?.serialNumberHex} />
                      <DetailRow
                        label={t("certificates.fingerprint")}
                        value={certificate?.fingerprintSha256}
                      />
                      {certificate?.issuingHubjectRootCertificateId ? (
                        <DetailRow
                          label={t("certificates.issuedUnderRoot")}
                          value={
                            roots.find((root) => root.hubjectRootCertificateId === certificate.issuingHubjectRootCertificateId)?.subjectCommonName
                            || certificate.issuingHubjectRootCertificateId
                          }
                        />
                      ) : null}
                      <DetailRow
                        label={t("certificates.chainStatus")}
                        value={chainStatusValue(certificate, installedSerials, t)}
                      />
                      {failed && certificate?.failureReason ? (
                        <DetailRow label={t("common.error")} value={certificate.failureReason} />
                      ) : null}
                      {canRenew && connector.chargingConnectorPublicId ? (
                        <div className="flex justify-end gap-2 pt-4">
                          <button
                            className="btn btn-outline-primary"
                            disabled={provisionDisabled}
                            onClick={() =>
                              requestProvision(
                                connector.chargingConnectorPublicId,
                                "renew",
                                title
                              )
                            }
                          >
                            {t("certificates.renew")}
                          </button>
                        </div>
                      ) : null}
                    </FormCard>
                  );
                })}
              </FormGrid>
            )}
          </FormSection>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center justify-end gap-4">
              <span className="text-sm text-mute-500">
                {isOffline
                  ? t("certificates.chargerMustBeOnline")
                  : !isHubjectRegistered
                    ? t("certificates.hubjectBeforeGenerate")
                    : ""}
              </span>
              <button
                className="btn btn-primary"
                disabled={provisionDisabled}
                onClick={() =>
                  requestProvision(
                    undefined,
                    issuedConnectorCount > 0 ? "renew" : "create",
                    ""
                  )
                }
              >
                {chargerActionLabel}
              </button>
            </div>
          </div>
        </>
      )}
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        okText={confirmState.okText}
        onClose={closeConfirm}
        onConfirm={() => {
          closeConfirm();
          confirmState.confirm();
        }}
      />
    </div>
  );
};

export default PointCertificates;
